import React, { ReactNode, useMemo, useState } from "react";

import { Alert } from "antd";
import cn from "classnames";
import classNames from "classnames";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import { DangerSVG, TickSVG } from "assets/icons";
import { ContentBody } from "components/ContentBody";
import { HowToUpgradeToTier4Modal } from "components/HowToUpgradeToTier4Modal";
import { StatusBadge } from "components/StatusBadge";
import { StatusButton } from "components/StatusButton";
import { TierBadge } from "components/TierBadge";
import { tire2PathMap, TierInfoModal, useTierInfoModal } from "components/TierInfoModal";
import { Tooltip } from "components/Tooltip/Tooltip";
import { Tier4AccountBtnTooltip } from "components/TooltipsContent";
import { PROFILE_TIER } from "constants/account";
import { PENDING_REVIEW_TIME, PROCESS_STATUS } from "constants/process";
import { usePermissions } from "contexts/permissionCtx";
import { useUser, useWindowSize } from "hooks";
import { useHasPermission } from "hooks/useHasPermission";
import { ProfileTierExclude0, TierAgreement } from "types/account";
import { ProcessStatus } from "types/process";

import {
  getTierBadgesMap,
  infoSectionTextMap,
  infroSectionIconsMap,
  requirements,
  tierIconsMap,
  whatYouUnlock,
} from "./consts";
import { Tier4HNWIconSVG } from "./icons";

import styles from "./TierBox.module.scss";

type Props = {
  tier: ProfileTierExclude0;
  currentStatus: ProcessStatus;
  lastStatus?: ProcessStatus;
  enabled: boolean;
  showPerks: boolean;
  className?: string;
  tooltip?: ReactNode;
  expirationDate?: string;
  displayTimeAlert?: boolean;
  title?: React.ReactNode;
};

const checkTierAgreement = (data: TierAgreement[], agreementKey: string) => {
  return data.some((agreement) => agreement.key === agreementKey);
};

export const TierBox = ({
  tier,
  currentStatus,
  lastStatus,
  enabled,
  className,
  tooltip,
  expirationDate,
  displayTimeAlert,
  title,
}: Props) => {
  const { data } = useUser();
  const {
    permissions: { isRemediationRequired },
  } = usePermissions();
  const { isMobile } = useWindowSize();
  const navigate = useNavigate();
  const { open, isOpenFromReadmeBtn, openModal, closeModal } = useTierInfoModal();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const isTier3 = tier === PROFILE_TIER.tier_3;
  const isTier2 = tier === PROFILE_TIER.tier_2;

  const isPending = lastStatus === PROCESS_STATUS.pending;
  const isAccepted = currentStatus === PROCESS_STATUS.accepted;
  const isRejected = currentStatus === PROCESS_STATUS.rejected;
  const isExpired = currentStatus === PROCESS_STATUS.expired;
  const isSelfCertificationAccepted = tier === PROFILE_TIER.tier_2 && isAccepted;

  const shouldRequireHNW =
    tier === PROFILE_TIER.tier_4 &&
    (data.tier === PROFILE_TIER.tier_1 ||
      data.tier === PROFILE_TIER.tier_1_uk_restricted ||
      data.tier === PROFILE_TIER.tier_2) &&
    !data.isHighNetWorth;

  const shouldDisplayStatusBtnTooltip = tier === PROFILE_TIER.tier_3 || (!enabled && shouldRequireHNW);

  const statusBtnTooltipContent = useMemo(() => {
    if (tier === PROFILE_TIER.tier_3) {
      return "No longer available to new applicants";
    } else if (tier === PROFILE_TIER.tier_4) {
      return <Tier4AccountBtnTooltip onClick={() => setIsUpgradeModalOpen(true)} />;
    }
  }, [tier]);

  const status = tier === PROFILE_TIER.tier_3 ? "discontinued" : currentStatus;

  const tierAgreementKey = `${tier}_agreement`;

  const customStatusButtonText = useMemo(() => {
    // Tier 3 is discontinued: nothing to start, whatever the account's tier
    if (isTier3) {
      return "Not available";
    }

    if (shouldRequireHNW) {
      return "Requires to be HNW";
    }

    return undefined;
  }, [data.isHighNetWorth, data.tier, tier]);

  const tierBadgesMap = useMemo(() => getTierBadgesMap(data.UKFinProm), [data.UKFinProm]);

  const handleClickStatusButton = () => {
    const tierAgreements = data.tierAgreements[tier];

    if (checkTierAgreement(tierAgreements, tierAgreementKey) || tier === PROFILE_TIER.tier_2) {
      navigate(tire2PathMap[tier]);
    } else {
      openModal();
    }
  };

  return (
    <>
      <ContentBody
        className={cn(styles.tierBox, className, {
          [styles.disabled]: tier === PROFILE_TIER.tier_3 || isRemediationRequired,
        })}
      >
        <div>
          <div className={styles.header}>
            {!isMobile && tierBadgesMap[tier]}
            {isMobile && <TierBadge tier={tier} />}
            {tier === PROFILE_TIER.tier_4 && (
              <div className={styles.hnwLogo}>
                <Tier4HNWIconSVG />
              </div>
            )}
          </div>

          {!!title && <div className={styles.title}>{title}</div>}

          <div className={styles.tierIcon}>
            <img src={tierIconsMap[tier]} />
          </div>
        </div>

        {isSelfCertificationAccepted && (
          <Tooltip title='Self-Certification can not be updated'>
            <StatusButton
              tier={tier}
              isPending={isPending}
              accepted={false}
              isRejected={isRejected}
              isExpired={isExpired}
              disabled
              className={styles.statusBtn}
              onClick={() => navigate(tire2PathMap[tier])}
              data-cy={`tier-btn__${tier}`}
            >
              Update
            </StatusButton>
            <span />
          </Tooltip>
        )}

        {!isSelfCertificationAccepted && (
          <Tooltip shouldDisplay={shouldDisplayStatusBtnTooltip} title={statusBtnTooltipContent}>
            <span className={styles.tooltipPlaceholder}></span>
            <StatusButton
              tier={tier}
              isPending={isPending}
              accepted={isAccepted}
              isRejected={isRejected}
              isExpired={isExpired}
              disabled={!enabled || isPending || isTier3}
              className={styles.statusBtn}
              onClick={handleClickStatusButton}
              data-cy={`tier-btn__${tier}`}
            >
              {customStatusButtonText}
            </StatusButton>
          </Tooltip>
        )}

        <div className={styles.statusBox}>
          Status:
          <StatusBadge
            iconPosition='right'
            isTierBox
            status={status}
            pendingUpTo={PENDING_REVIEW_TIME}
            data-cy={`tier-status__${tier}__${status}`}
          />
        </div>

        {displayTimeAlert && expirationDate && (
          <div>
            {
              <div className={styles.timeAlert}>
                <Alert
                  style={{ maxWidth: 190 }}
                  icon={<DangerSVG />}
                  type='success'
                  message={
                    <>
                      <b>Note! </b>
                      <span>{`Your active Certification remains valid until: ${DateTime.fromISO(
                        expirationDate
                      ).toFormat("dd LLL, yyyy")}`}</span>
                    </>
                  }
                />
              </div>
            }
          </div>
        )}

        <div className={styles.bottomSection}>
          <p className={styles.bottomSectionHeader}>{isTier3 ? "NOTE" : isTier2 ? "Purpose" : "What you unlock"}</p>

          {isTier3 ? (
            <p>
              This tier is no longer available for new applicants. It remains active only for existing members who
              qualified under previous regulations. To access Private Equity today, please update your Tier 1 Investor
              Suitability to declare a Net Worth of $1M+, then proceed to Tier 4.
            </p>
          ) : isTier2 ? (
            <p>This is a mandatory intermediate step to prepare for Tier 4. It confirms your eligibility path.</p>
          ) : (
            <>
              {whatYouUnlock[tier].map((unlock) => (
                <div className={styles.requirement} key={`${tier}-${unlock}`}>
                  <TickSVG /> <span>{unlock}</span>
                </div>
              ))}
            </>
          )}
        </div>

        <div className={classNames(styles.infoSection, { [styles.tier3Info]: isTier3 || isTier2 })}>
          <div>
            <p>{infoSectionTextMap[tier]}</p>
          </div>

          {tier !== PROFILE_TIER.tier_3 && tier !== PROFILE_TIER.tier_2 && (
            <a onClick={() => openModal(true)}>Read more</a>
          )}
        </div>
      </ContentBody>

      <TierInfoModal tier={tier} open={open} openReadme={isOpenFromReadmeBtn} handleClose={closeModal} />

      {tier === PROFILE_TIER.tier_4 && (
        <HowToUpgradeToTier4Modal open={isUpgradeModalOpen} handleClose={() => setIsUpgradeModalOpen(false)} />
      )}
    </>
  );
};
